import { format } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';

// IST timezone constant
export const IST_TIMEZONE = 'Asia/Kolkata'; // GMT+5:30

/**
 * Parse server timestamp safely.
 * If timezone is missing, treat it as UTC to avoid GMT/IST drift.
 */
export function parseServerDate(date) {
  if (!date) return null;

  if (date instanceof Date) {
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof date === 'string') {
    const dateString = date.trim();
    let parsed;

    if (dateString.includes('Z') || dateString.match(/[+-]\d{2}:\d{2}$/)) {
      parsed = new Date(dateString);
    } else if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/) || dateString.includes('T')) {
      parsed = new Date(`${dateString}Z`);
    } else {
      parsed = new Date(dateString);
    }

    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = new Date(date);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Convert a date to IST timezone
 */
export function toIST(date) {
  if (!date) return null;
  const dateObj = date instanceof Date ? date : new Date(date);
  return utcToZonedTime(dateObj, IST_TIMEZONE);
}

/**
 * Convert IST date to UTC for API calls
 */
export function fromIST(date) {
  if (!date) return null;
  const dateObj = date instanceof Date ? date : new Date(date);
  return zonedTimeToUtc(dateObj, IST_TIMEZONE);
}

/**
 * Format date in IST timezone
 * Handles UTC dates from database and converts them to IST for display
 */
export function formatIST(date, formatStr = 'MMM dd, yyyy, h:mm a') {
  if (!date) return '';

  const dateObj = parseServerDate(date);
  if (!dateObj) {
    console.warn('Invalid date:', date);
    return '';
  }
  
  // Convert UTC date to IST timezone, then format
  // First convert to IST zoned time, then format using date-fns format
  const istDate = utcToZonedTime(dateObj, IST_TIMEZONE);
  return format(istDate, formatStr);
}

/**
 * Get current date in IST
 */
export function getCurrentISTDate() {
  return toIST(new Date());
}

/**
 * Format date for display (IST)
 */
export function formatDate(date, formatStr = 'MMM dd, yyyy') {
  return formatIST(date, formatStr);
}

/**
 * Format datetime for display (IST)
 */
export function formatDateTime(date, formatStr = 'MMM dd, yyyy, h:mm a') {
  return formatIST(date, formatStr);
}

/**
 * Format time for display (IST)
 */
export function formatTime(date, formatStr = 'h:mm a') {
  return formatIST(date, formatStr);
}

/**
 * Get date string in YYYY-MM-DD format for date inputs (IST)
 */
export function getDateInputValue(date) {
  if (!date) return '';
  const istDate = toIST(date instanceof Date ? date : new Date(date));
  return format(istDate, 'yyyy-MM-dd');
}

/**
 * Get datetime string for datetime-local inputs (IST)
 */
export function getDateTimeInputValue(date) {
  if (!date) return '';
  const istDate = toIST(date instanceof Date ? date : new Date(date));
  const year = istDate.getFullYear();
  const month = String(istDate.getMonth() + 1).padStart(2, '0');
  const day = String(istDate.getDate()).padStart(2, '0');
  const hours = String(istDate.getHours()).padStart(2, '0');
  const minutes = String(istDate.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Parse datetime-local input value to Date object (treating as IST)
 */
export function parseDateTimeInputValue(value) {
  if (!value) return null;
  // The input value is in local format, but we treat it as IST
  const [datePart, timePart] = value.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);
  
  // Create date in IST timezone
  const istDate = new Date(year, month - 1, day, hours, minutes);
  // Convert to UTC for storage
  return fromIST(istDate);
}

/**
 * Get today's date in IST for date calculations
 */
export function getTodayIST() {
  return getCurrentISTDate();
}

/**
 * Add days to a date in IST
 */
export function addDaysIST(date, days) {
  const istDate = toIST(date instanceof Date ? date : new Date(date));
  istDate.setDate(istDate.getDate() + days);
  return istDate;
}

/**
 * Check if two dates are the same day in IST
 */
export function isSameDayIST(date1, date2) {
  if (!date1 || !date2) return false;
  const d1 = toIST(date1 instanceof Date ? date1 : parseServerDate(date1));
  const d2 = toIST(date2 instanceof Date ? date2 : parseServerDate(date2));
  if (!d1 || !d2) return false;
  return d1.getFullYear() === d2.getFullYear() &&
         d1.getMonth() === d2.getMonth() &&
         d1.getDate() === d2.getDate();
}

/**
 * Format time for display in 12-hour format (IST)
 */
export function formatTime12(date) {
  return formatIST(date, 'h:mm a');
}

/**
 * Format date & 12-hour time for chat messages (IST)
 * e.g. "Today · 4:54 PM", "Yesterday · 4:54 PM", or "Aug 27, 2026 · 4:54 PM"
 */
export function formatChatDateTime(date) {
  if (!date) return '';
  const dateObj = parseServerDate(date);
  if (!dateObj) return '';
  const today = getTodayIST();
  const istDate = toIST(dateObj);

  if (isSameDayIST(today, istDate)) {
    return `Today · ${format(istDate, 'h:mm a')}`;
  }

  const yesterday = addDaysIST(today, -1);
  if (isSameDayIST(yesterday, istDate)) {
    return `Yesterday · ${format(istDate, 'h:mm a')}`;
  }

  return format(istDate, 'MMM dd, yyyy · h:mm a');
}

/**
 * Format last texted date & 12-hour time for guest sidebar (IST)
 * e.g. "Today, 4:54 PM", "Yesterday, 4:54 PM", or "Aug 27, 4:54 PM"
 */
export function formatLastMessageDate(date) {
  if (!date) return '';
  const dateObj = parseServerDate(date);
  if (!dateObj) return '';
  const today = getTodayIST();
  const istDate = toIST(dateObj);

  if (isSameDayIST(today, istDate)) {
    return `Today, ${format(istDate, 'h:mm a')}`;
  }

  const yesterday = addDaysIST(today, -1);
  if (isSameDayIST(yesterday, istDate)) {
    return `Yesterday, ${format(istDate, 'h:mm a')}`;
  }

  return format(istDate, 'MMM dd, h:mm a');
}

